// Zoll Search Agent frontend logic

const ZOLL_SEARCH_AGENT_CONFIG = {
  saveEndpoint: 'https://zoll-search-agent.zollantiques77.workers.dev/api/saved-searches',
  // The GET / PATCH / DELETE patterns used in account integration below
  listEndpoint: 'https://zoll-search-agent.zollantiques77.workers.dev/api/saved-searches',
};

function initSearchAgentForms() {
  document.querySelectorAll('.search-agent__form').forEach((form) => {
    const lang = document.documentElement.lang;
    
    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const submitBtn = form.querySelector('.search-agent__submit');
      const successEl = form.querySelector('.search-agent__success');
      const errorEl = form.querySelector('.search-agent__error');

      const email = form.elements.email?.value?.trim();
      const query = form.elements.query?.value?.trim();
      const consent = form.elements.consent?.checked;

      if (!email || !query || !consent) {
        if (errorEl) {
          errorEl.textContent = lang === 'en' 
            ? 'Please fill in email, search term and accept the terms.' 
            : 'Udfyld e-mail, søgning og godkend betingelserne.';
          errorEl.hidden = false;
        }
        if (successEl) successEl.hidden = true;
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = lang === 'en' ? 'Saving...' : 'Gemmer...';
      }
      if (errorEl) errorEl.hidden = true;

      try {
        // Send JSON exactly as the Worker expects it
        const payload = {
          email: email,
          query_raw: query,          // Worker reads `query_raw`
          query: query,              // Optional extra
          consent: true,
          source: 'shopify_frontend',
        };

        const response = await fetch(ZOLL_SEARCH_AGENT_CONFIG.saveEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }

        if (successEl) successEl.hidden = false;
        if (errorEl) errorEl.hidden = true;

        // 🔔 SHOW CONFIRMATION POPUP
        const queryInput = form.elements.query;
        const term = queryInput ? queryInput.value : '';

        const title = form.dataset.saPopupTitle;
        const bodyTemplate = form.dataset.saPopupBody;

        if (title && bodyTemplate) {
          showSearchAgentPopup({
          title,
          messageTemplate: bodyTemplate,
          term
        });
        }

        // LIVE UPDATE: Refresh the list after successful save
        form.reset(); // Clear the form

        const accountContainer = document.querySelector('[data-account-search-agents]');
        if (accountContainer) {
          const email = accountContainer.dataset.customerEmail;
          if (email) {
            fetchSavedSearches(email).then((data) => {
              renderSavedSearches(accountContainer, data);
            });
          }
        }
      } catch (err) {
        console.error('Search agent error', err);
        if (errorEl) {
          errorEl.textContent = lang === 'en' 
            ? 'Something went wrong. Please try again in a moment.' 
            : 'Noget gik galt. Prøv igen om et øjeblik.';
          errorEl.hidden = false;
        }
        if (successEl) successEl.hidden = true;
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = lang === 'en' ? 'Activate search alert' : 'Aktiver søgeagent';
        }
      }
    });
  });
}

/* ---------------------
   Account page helpers
--------------------- */

async function fetchSavedSearches(email) {
  const url = new URL(ZOLL_SEARCH_AGENT_CONFIG.listEndpoint);
  url.searchParams.set('email', email);

  const res = await fetch(url.toString(), { method: 'GET' });
  if (!res.ok) {
    throw new Error('Failed to load saved searches: ' + res.status);
  }
  return res.json();
}

async function updateSavedSearch(id, data) {
  const url = `${ZOLL_SEARCH_AGENT_CONFIG.listEndpoint}/${encodeURIComponent(id)}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new Error('Failed to update search: ' + res.status);
  }
  return res.json();
}

async function deleteSavedSearch(id) {
  const url = `${ZOLL_SEARCH_AGENT_CONFIG.listEndpoint}/${encodeURIComponent(id)}`;
  const res = await fetch(url, { method: 'DELETE' });
  if (!res.ok) {
    throw new Error('Failed to delete search: ' + res.status);
  }
  return true;
}

function renderSavedSearches(container, data) {
  const list = Array.isArray(data?.searches) ? data.searches : [];
  const lang = document.documentElement.lang;

  if (!list.length) {
    const noAlertsText = lang === 'en' ? 'You have no active search alerts yet.' : 'Du har ingen aktive søgeagenter endnu.';
    container.innerHTML = `<p>${noAlertsText}</p>`;
    return;
  }

  const itemsHtml = list
    .map((item) => {
      const id = item.id || item.uuid || item._id || '';
      const query = item.query || item.query_raw || '';
      const createdAt = item.created_at || item.createdAt || '';
      const paused = !!item.paused;
      const encodedQuery = encodeURIComponent(query || '');

      const dateLabel = createdAt
        ? new Date(createdAt).toLocaleDateString(lang === 'en' ? 'en-GB' : 'da-DK')
        : '';

      // Bilingual text
      const createdText = lang === 'en' ? 'Created' : 'Oprettet';
      const pausedText = lang === 'en' ? 'Paused' : 'Sat på pause';
      const pauseButtonText = paused 
        ? (lang === 'en' ? 'Activate again' : 'Aktivér igen')
        : (lang === 'en' ? 'Pause' : 'Sæt på pause');
      const deleteButtonText = lang === 'en' ? 'Delete' : 'Slet';

      return `
        <article class="account-search-agent"
          data-search-agent-id="${id}"
          data-search-agent-query="${encodedQuery}">
          <div class="account-search-agent__main">
            <h3 class="account-search-agent__query">"${query}"</h3>
            <p class="account-search-agent__meta">
              ${createdText}: ${dateLabel || '-'}
              ${paused ? ` • <span class="account-search-agent__badge account-search-agent__badge--paused">${pausedText}</span>` : ''}
            </p>
          </div>
          <div class="account-search-agent__actions">
            <button type="button"
              class="button button--secondary button--small js-search-agent-toggle"
              data-paused="${paused ? 'true' : 'false'}">
              ${pauseButtonText}
            </button>
            <button type="button"
              class="button button--tertiary button--small js-search-agent-delete">
              ${deleteButtonText}
            </button>
          </div>
        </article>
      `;
    })
    .join('');

  container.innerHTML = itemsHtml;
  bindAccountSearchAgentRowEvents(container);
}

function bindAccountSearchAgentRowEvents(container) {
  const cards = container.querySelectorAll('.account-search-agent');

  cards.forEach((card) => {
    const id = card.dataset.searchAgentId;
    if (!id) return;

    const deleteBtn = card.querySelector('.js-search-agent-delete');
    const toggleBtn = card.querySelector('.js-search-agent-toggle');

    // --- DELETE ---
    if (deleteBtn && deleteBtn.dataset.bound !== 'true') {
      deleteBtn.dataset.bound = 'true';

      deleteBtn.addEventListener('click', async (event) => {
        event.preventDefault();

        const lang = document.documentElement.lang;

        try {
          await deleteSavedSearch(id);
          card.remove();

          if (!container.querySelector('.account-search-agent')) {
            const noAlertsText = lang === 'en' ? 'You have no active search alerts.' : 'Du har ingen aktive søgeagenter.';
            container.innerHTML = `<p>${noAlertsText}</p>`;
          }
        } catch (err) {
          console.error(err);
          const alertText = lang === 'en' 
            ? 'Could not delete the search alert. Please try again.' 
            : 'Kunne ikke slette søgeagenten. Prøv igen.';
          alert(alertText);
        }
      });
    }

    // --- PAUSE / AKTIVÉR ---
    if (toggleBtn && toggleBtn.dataset.bound !== 'true') {
      toggleBtn.dataset.bound = 'true';

      toggleBtn.addEventListener('click', async (event) => {
        event.preventDefault();

        const lang = document.documentElement.lang;
        const btn = toggleBtn;
        const paused = btn.dataset.paused === 'true';

        btn.disabled = true;
        btn.textContent = lang === 'en' ? 'Updating...' : 'Opdaterer...';

        try {
          await updateSavedSearch(id, { paused: !paused });
          btn.dataset.paused = !paused ? 'true' : 'false';
          
          // Bilingual button text
          if (!paused) {
            btn.textContent = lang === 'en' ? 'Activate again' : 'Aktivér igen';
          } else {
            btn.textContent = lang === 'en' ? 'Pause' : 'Sæt på pause';
          }

          const badge = card.querySelector('.account-search-agent__badge');

          if (!paused) {
            // Sat på pause → sørg for at badge findes
            if (!badge) {
              const meta = card.querySelector('.account-search-agent__meta');
              if (meta) {
                const span = document.createElement('span');
                span.className = 'account-search-agent__badge account-search-agent__badge--paused';
                span.textContent = lang === 'en' ? 'Paused' : 'Sat på pause';
                meta.insertAdjacentHTML('beforeend', ' • ');
                meta.appendChild(span);
              }
            }
          } else {
            // Aktiveret igen → fjern badge
            if (badge) badge.remove();
          }
        } catch (err) {
          console.error(err);
          const alertText = lang === 'en' 
            ? 'Could not update the search alert. Please try again.' 
            : 'Kunne ikke opdatere søgeagenten. Prøv igen.';
          alert(alertText);
        } finally {
          btn.disabled = false;
        }
      });
    }
  });
}

function initAccountSearchAgents() {
  const container = document.querySelector('[data-account-search-agents]');
  if (!container) return;

  const lang = document.documentElement.lang;
  const email = container.dataset.customerEmail;
  
  if (!email) {
    const loginText = lang === 'en' 
      ? 'Log in to see your search alerts.' 
      : 'Log ind for at se dine søgeagenter.';
    container.innerHTML = `<p>${loginText}</p>`;
    return;
  }

  const loadingText = lang === 'en' 
    ? 'Loading your search alerts...' 
    : 'Indlæser dine søgeagenter…';
  container.innerHTML = `<p>${loadingText}</p>`;

  fetchSavedSearches(email)
    .then((data) => {
      renderSavedSearches(container, data);
    })
    .catch((err) => {
      console.error(err);
      const errorText = lang === 'en'
        ? 'We could not load your search alerts right now. Please try again later.'
        : 'Vi kunne ikke hente dine søgeagenter lige nu. Prøv igen senere.';
      container.innerHTML = `<p>${errorText}</p>`;
    });
}

function showSearchAgentPopup({ title, messageTemplate, term }) {
  let modal = document.querySelector('.sa-modal');

  if (!modal) {
    modal = document.createElement('div');
    modal.className = 'sa-modal';
    modal.innerHTML = `
      <div class="sa-modal__backdrop"></div>
      <div class="sa-modal__panel" role="dialog" aria-modal="true">
        <button class="sa-modal__close" aria-label="Close">×</button>
        <h2 class="sa-modal__title"></h2>
        <p class="sa-modal__message"></p>
      </div>
      <style>
        .sa-modal {
          position: fixed;
          inset: 0;
          z-index: 9999;
        }
        .sa-modal__backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.5);
        }
        .sa-modal__panel {
          position: relative;
          max-width: 420px;
          margin: 15vh auto;
          background: #fff;
          padding: 1.5rem;
          border-radius: 6px;
          border: 1px solid #000;
          z-index: 1;
          text-align: center;
        }
        .sa-modal__close {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          background: none;
          border: 0;
          font-size: 1.5rem;
          cursor: pointer;
        }
      </style>
    `;
    document.body.appendChild(modal);

    const close = () => modal.remove();
    modal.querySelector('.sa-modal__close').addEventListener('click', close);
    modal.querySelector('.sa-modal__backdrop').addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.body.contains(modal)) {
        close();
      }
    });
  }

  // Title
  modal.querySelector('.sa-modal__title').textContent = title;

  // Message (DOM-safe, bold term)
  const messageEl = modal.querySelector('.sa-modal__message');
  messageEl.textContent = '';

  if (messageTemplate && messageTemplate.includes('%TERM%')) {
    const [before, after] = messageTemplate.split('%TERM%');

    messageEl.appendChild(document.createTextNode(before || ''));

    if (term) {
      const strong = document.createElement('strong');
      strong.textContent = term;
      messageEl.appendChild(strong);
    }

    messageEl.appendChild(document.createTextNode(after || ''));
  } else {
    messageEl.textContent = messageTemplate || '';
  }

  modal.querySelector('.sa-modal__close').focus();
}

document.addEventListener('DOMContentLoaded', () => {
  initSearchAgentForms();
  initAccountSearchAgents();
});
